import logger from '@ayocore/platform/common/helpers/logger';

type ControllerDebugGlobal = typeof globalThis & {
  __CONTROLLER_DEBUG__?: boolean;
};

function isControllerDebugEnabled(): boolean {
  // `?.`: под vite env есть всегда, а в tsx-раннерах (headless-тесты) import.meta.env
  // отсутствует — без гарда первый setState падал бы с TypeError
  if (!import.meta.env?.DEV) return false;
  const runtime = globalThis as ControllerDebugGlobal;
  return runtime.__CONTROLLER_DEBUG__ === true;
}

/**
 * Базовый store для контроллеров UI (канон FRONTEND_STATE: Controller + useSyncExternalStore).
 *
 * Назначение:
 * - хранить внешний state для useSyncExternalStore;
 * - давать единый subscribe/getSnapshot API;
 * - change-detection: setState без фактических изменений не будит подписчиков;
 * - поддерживать локальный batching обновлений внутри контроллера.
 *
 * Отладка (только dev): `globalThis.__CONTROLLER_DEBUG__ = true` — логирование
 * setState/notify с именем стора и списком изменённых ключей.
 *
 * Поднят из CRM Activity (`components/Activity/controllers/ControllerStore.ts`,
 * там остался реэкспорт) как общий примитив платформы.
 */
export class ControllerStore<TState extends object> {
  private listeners = new Set<() => void>();

  private state: TState;

  private batchDepth = 0;

  private batchDirty = false;

  constructor(initialState: TState) {
    this.state = initialState;
  }

  /**
   * Подписка для useSyncExternalStore.
   */
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * Снимок текущего состояния.
   */
  getSnapshot = (): TState => this.state;

  /**
   * Частичное обновление состояния с нотификацией подписчиков.
   */
  protected setState(patch: Partial<TState>): void {
    const previousState = this.state;
    const patchEntries = Object.entries(patch) as Array<[keyof TState, unknown]>;
    if (patchEntries.length === 0) {
      return;
    }

    const patchKeys = patchEntries.map(([key]) => String(key));
    const changedKeys = patchEntries
      .filter(([key, value]) => !Object.is(previousState[key], value as TState[keyof TState]))
      .map(([key]) => String(key));

    if (changedKeys.length === 0) {
      if (isControllerDebugEnabled()) {
        logger.info('[Controller:Debug] ControllerStore.setState skipped (no changes)', {
          store: this.constructor.name,
          patchKeys,
          batchDepth: this.batchDepth,
          listeners: this.listeners.size,
        });
      }
      return;
    }

    this.state = { ...previousState, ...patch };

    if (isControllerDebugEnabled()) {
      logger.info('[Controller:Debug] ControllerStore.setState', {
        store: this.constructor.name,
        patchKeys,
        changedKeys,
        batchDepth: this.batchDepth,
        listeners: this.listeners.size,
      });
    }

    this.notifyIfNotBatched();
  }

  /**
   * Локальный batching обновлений состояния внутри одного контроллера.
   */
  protected batch(action: () => void): void {
    this.batchDepth += 1;
    try {
      action();
    } finally {
      this.batchDepth -= 1;
      if (this.batchDepth === 0 && this.batchDirty) {
        this.batchDirty = false;
        this.notify();
      }
    }
  }

  /**
   * Очистка подписчиков при dispose контроллера.
   */
  dispose(): void {
    this.listeners.clear();
  }

  /**
   * Доступ к текущему состоянию для наследников.
   */
  protected getState(): TState {
    return this.state;
  }

  private notifyIfNotBatched(): void {
    if (this.batchDepth > 0) {
      this.batchDirty = true;
      return;
    }
    this.notify();
  }

  private notify(): void {
    if (isControllerDebugEnabled()) {
      logger.info('[Controller:Debug] ControllerStore.notify', {
        store: this.constructor.name,
        listeners: this.listeners.size,
      });
    }
    this.listeners.forEach(listener => listener());
  }
}
